import { inject } from '@angular/core';
import { Router, type CanMatchFn } from '@angular/router';
import { SessionStore } from './state/session.store';

/**
 * Empêche un rôle d'atteindre un écran hors de son périmètre en tapant l'URL. L'écran
 * « Vos employés » n'est par exemple pas accessible au Collaborateur (SPEC-PIN-008 §6) : masquer
 * l'entrée du menu ne suffit pas.
 *
 * ⚠️ Ce garde est un confort d'ergonomie, **pas une mesure de sécurité**. La règle qui protège
 * réellement les données est le contrôle serveur appliqué dans les query handlers, avant tout
 * filtre et toute pagination (RM-PIN-195, RM-ACC8-003/004).
 */
export const roleAccessGuard: CanMatchFn = (route, segments) => {
  const session = inject(SessionStore);
  const router = inject(Router);

  const path = '/' + segments.map((segment) => segment.path).join('/');

  /*
   * Un écran de détail n'a pas d'entrée de menu : il déclare celle dont il dépend via
   * `data.navPath`. On ne compare pas sur un préfixe, car deux entrées distinctes sont imbriquées
   * et n'ont pas le même périmètre — « /flux » ne doit pas ouvrir « /flux/packs », réservé à
   * l'Administrateur parc IT (SPEC-PIN-002 §6).
   */
  const required = (route.data?.['navPath'] as string | undefined) ?? path;
  // Périmètre de navigation de l'utilisateur : permissions réelles en mode branché, rôle en démo.
  const allowed = session.navEntries();

  // Périmètre indéterminé (mode branché, /api/me pas encore résolu) : on laisse rendre — le serveur
  // reste la barrière (L3-02). Rediriger ici provoquerait une boucle tant que rien n'est chargé.
  if (allowed.length === 0) {
    return true;
  }

  if (allowed.some((entry) => entry.path === required)) {
    return true;
  }

  // Redirection vers le premier écran autorisé du rôle plutôt qu'une page d'erreur :
  // l'utilisateur n'a rien fait de fautif, il a suivi un lien qui ne le concerne pas.
  return router.parseUrl(allowed[0]?.path ?? '/tableau-de-bord');
};
